import { type ApiClient, noContentSchema } from "@simpro/api-client";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Skeleton, toast } from "@simpro/ui";
import { type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Monitor, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
  type PushConfig,
  pushConfigSchema,
  pushDeviceSchema,
  registeredDeviceSchema,
  testPushSchema,
} from "./push.schemas";
import { MESSAGE_TYPE, type PushMessageToPage } from "./sw/swCore";
import { PUSH_WORKER_PATH } from "./workerPath";

export interface CreatePushOptions {
  /** This app's name as the server knows it: `salon`, `invoice`. A push opens in the app it arrives through. */
  app: string;
  /** API prefix. Default `/api/v1`. */
  basePath?: string;
}

/** Where the browser stands, before any server answer: whether push can be asked for at all. */
export type PushPermission = "unsupported" | "default" | "granted" | "denied";

const WORKER_SCOPE = "/push/";
const FIREBASE_APP_NAME = "simpro-push";
/** "Not now" on the soft-ask holds for this long (push design 11.4). */
const DISMISS_DAYS = 30;

/** Per-viewer conveniences only (a remembered token, a dismissal), so storage failing never breaks anything. */
function readStorage(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    // Private window or blocked storage: the device simply registers again next time.
  }
}

/** What the browser can do, read fresh each time because the person can change it in site settings. */
export function browserPermission(): PushPermission {
  if (
    typeof window === "undefined" ||
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return "unsupported";
  }

  return Notification.permission;
}

/** The first label whose marker the user agent contains, checked in order (Edge also says Chrome). */
function firstMatch(userAgent: string, table: readonly (readonly [string, string])[], otherwise: string): string {
  return table.find(([marker]) => userAgent.includes(marker))?.[1] ?? otherwise;
}

/** "Chrome on Windows": enough to tell your devices apart, not enough to fingerprint one. */
export function deviceLabel(userAgent: string): string {
  const browser = firstMatch(
    userAgent,
    [
      ["Edg/", "Edge"],
      ["Firefox/", "Firefox"],
      ["Chrome/", "Chrome"],
      ["Safari/", "Safari"],
    ],
    "Browser",
  );
  const system = firstMatch(
    userAgent,
    [
      ["Windows", "Windows"],
      ["Android", "Android"],
      ["iPhone", "iOS"],
      ["iPad", "iOS"],
      ["Mac OS X", "macOS"],
      ["Linux", "Linux"],
    ],
    "a device",
  );
  return `${browser} on ${system}`;
}

/** Waits until the push worker is active: Firebase subscribes through it, and an installing worker cannot. */
async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(PUSH_WORKER_PATH, { scope: WORKER_SCOPE });
  if (registration.active) return registration;

  const worker = registration.installing ?? registration.waiting;
  if (worker) {
    await new Promise<void>((resolve) => {
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") resolve();
      });
    });
  }

  return registration;
}

/**
 * Push in a staff app (push design phase 3, FR-NT-064 to FR-NT-071): turning it on in this browser,
 * keeping the registration fresh, removing it at sign-out, and showing a push that arrives while the
 * app is in front. Firebase is imported only here, and only when push is used, so it adds nothing to
 * the first page load (NFR-NT-017).
 */
export function createPush(apiClient: ApiClient, options: CreatePushOptions) {
  const api = `${options.basePath ?? "/api/v1"}/notifications/push`;
  const tokenKey = `simpro.push.${options.app}.token`;
  const dismissKey = `simpro.push.${options.app}.dismissedUntil`;

  const pushKeys = {
    all: ["push", options.app] as const,
    config: () => [...pushKeys.all, "config"] as const,
    devices: () => [...pushKeys.all, "devices"] as const,
  };

  /** The FCM token for this browser, or a stand-in when the server's provider is `logging` (no Firebase). */
  async function obtainToken(config: PushConfig): Promise<string> {
    if (config.firebase === null || config.vapidKey === null) {
      return readStorage(tokenKey) ?? `logging-${crypto.randomUUID()}`;
    }

    const [{ getApps, initializeApp }, messaging] = await Promise.all([
      import("firebase/app"),
      import("firebase/messaging"),
    ]);
    if (!(await messaging.isSupported())) {
      throw new Error("This browser cannot receive push notifications.");
    }

    const app =
      getApps().find((a) => a.name === FIREBASE_APP_NAME) ?? initializeApp(config.firebase, FIREBASE_APP_NAME);
    return messaging.getToken(messaging.getMessaging(app), {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration: await activeRegistration(),
    });
  }

  async function register(token: string): Promise<void> {
    await apiClient.post(registeredDeviceSchema, `${api}/subscriptions`, {
      token,
      app: options.app,
      platform: "web",
      deviceLabel: deviceLabel(navigator.userAgent),
    });
    writeStorage(tokenKey, token);
  }

  /**
   * Removes this browser's registration while the session still works (push design FR-NT-068,
   * SEC-NT-022). Given to `createAuth` as `beforeSignOut`, so a shared PC stops receiving the last
   * person's notifications the moment they sign out. Never throws: signing out must not fail on it.
   */
  async function removeThisDevice(): Promise<void> {
    const token = readStorage(tokenKey);
    if (!token) return;

    writeStorage(tokenKey, null);
    await apiClient.post(noContentSchema, `${api}/subscriptions/remove`, { token }).catch(() => undefined);
  }

  function usePushConfig() {
    return useQuery({
      queryKey: pushKeys.config(),
      queryFn: () => apiClient.get(pushConfigSchema, `${api}/config`),
      staleTime: 5 * 60_000,
    });
  }

  function usePushDevices() {
    return useQuery({
      queryKey: pushKeys.devices(),
      queryFn: () => apiClient.get(z.array(pushDeviceSchema), `${api}/subscriptions`),
    });
  }

  /** Whether this browser is registered, as far as this browser knows. */
  function useThisDevice() {
    const [permission, setPermission] = useState<PushPermission>(browserPermission);
    const [registered, setRegistered] = useState(() => readStorage(tokenKey) !== null);
    const refresh = () => {
      setPermission(browserPermission());
      setRegistered(readStorage(tokenKey) !== null);
    };
    return { permission, registered, refresh };
  }

  function useEnablePush(onDone?: () => void) {
    const queryClient = useQueryClient();
    return useMutation({
      // It shows its own error toast; without this the app's global handler adds a second one.
      meta: { silent: true },
      mutationFn: async (config: PushConfig) => {
        // Asked only from a click on our own card, never on page load (FR-NT-071).
        const answer = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
        if (answer !== "granted") {
          throw new Error("Notifications are blocked for this site in the browser's settings.");
        }

        await register(await obtainToken(config));
      },
      onSuccess: () => {
        toast.success("Notifications are on", "You'll get them on this device even when the app is closed.");
        void queryClient.invalidateQueries({ queryKey: pushKeys.devices() });
      },
      onError: (error) => toast.error("Notifications could not be turned on", error.message),
      onSettled: () => onDone?.(),
    });
  }

  function useDisablePush(onDone?: () => void) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (config: PushConfig | undefined) => {
        await removeThisDevice();
        if (config?.firebase) {
          const [{ getApps }, messaging] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
          const app = getApps().find((a) => a.name === FIREBASE_APP_NAME);
          if (app) await messaging.deleteToken(messaging.getMessaging(app)).catch(() => undefined);
        }
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: pushKeys.devices() });
        onDone?.();
      },
    });
  }

  function useSendTestPush() {
    return useMutation({
      meta: { silent: true },
      mutationFn: () => apiClient.post(testPushSchema, `${api}/test`),
      onSuccess: (result) =>
        toast.info("Test sent", `It is on its way to ${result.devices === 1 ? "your device" : `your ${result.devices} devices`}.`),
      onError: (error) => toast.error("The test could not be sent", error.message),
    });
  }

  function useRemoveDevice() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => apiClient.delete(noContentSchema, `${api}/subscriptions/${encodeURIComponent(id)}`),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: pushKeys.devices() }),
    });
  }

  /**
   * Mount once inside the signed-in layout. It registers this browser again on every start, which
   * keeps it from being purged as idle and picks up a token Firebase rotated (FR-NT-067), and it
   * turns a push that arrives while a tab is in front into a toast and a fresh bell (FR-NT-065).
   */
  function PushBootstrap({ notificationQueryKey = ["notifications"] }: { notificationQueryKey?: QueryKey }) {
    const queryClient = useQueryClient();
    const config = usePushConfig();
    const started = useRef(false);

    useEffect(() => {
      const data = config.data;
      if (started.current || !data?.enabled || browserPermission() !== "granted" || readStorage(tokenKey) === null) {
        return;
      }

      started.current = true;
      void obtainToken(data)
        .then(register)
        .catch(() => undefined);
    }, [config.data]);

    useEffect(() => {
      const container = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
      if (!container) return;

      const onMessage = (event: MessageEvent<unknown>) => {
        const message = event.data as Partial<PushMessageToPage> | null;
        if (message?.type !== MESSAGE_TYPE || !message.notification) return;

        void queryClient.invalidateQueries({ queryKey: notificationQueryKey });
        toast({
          title: message.notification.title,
          description: message.notification.body,
          tone: message.notification.priority === "High" ? "warning" : "info",
        });
      };

      container.addEventListener("message", onMessage);
      container.startMessages?.();
      return () => container.removeEventListener("message", onMessage);
    }, [queryClient, notificationQueryKey]);

    return null;
  }

  /**
   * The soft-ask: an invitation to turn push on, shown only where it can work and only until the
   * person answers (push design 11.4, FR-NT-071). "Not now" holds for thirty days.
   */
  function PushSoftAsk({
    className,
    title = "Get notified on this device",
    description = "Requests and updates for you, even when this tab is closed.",
  }: {
    className?: string;
    /** The invitation's words; the booking app says "Remind me on this phone" (push design phase 6). */
    title?: string;
    description?: string;
  }) {
    const config = usePushConfig();
    const device = useThisDevice();
    const [dismissed, setDismissed] = useState(() => Number(readStorage(dismissKey) ?? 0) > Date.now());
    const enable = useEnablePush(device.refresh);

    if (!config.data?.enabled || device.permission !== "default" || device.registered || dismissed) {
      return null;
    }

    const notNow = () => {
      writeStorage(dismissKey, String(Date.now() + DISMISS_DAYS * 86_400_000));
      setDismissed(true);
    };

    return (
      <Card variant="muted" padding="md" role="region" aria-label="Turn on notifications" className={className}>
        <div className="flex flex-wrap items-center gap-4">
          <BellRing className="size-5 shrink-0 text-accent-500" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-heading">{title}</p>
            <p className="text-sm text-subtle">{description}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={notNow}>
              Not now
            </Button>
            <Button size="sm" loading={enable.isPending} onClick={() => config.data && enable.mutate(config.data)}>
              Turn on
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  /** "This device" and "Your devices" (push design 11.2, FR-NT-055, FR-NT-060). */
  /** @param allowTest Whether "Send a test" is offered; customers have no test route (push design phase 6). */
  function PushDevicesCard({ allowTest = true }: { allowTest?: boolean }) {
    const config = usePushConfig();
    const devices = usePushDevices();
    const device = useThisDevice();
    const enable = useEnablePush(device.refresh);
    const disable = useDisablePush(device.refresh);
    const test = useSendTestPush();
    const remove = useRemoveDevice();

    const status =
      device.permission === "unsupported"
        ? "This browser cannot receive notifications. On iPhone or iPad, add the app to your Home Screen first."
        : device.permission === "denied"
          ? "Notifications are blocked for this site. Allow them in the browser's site settings, then come back."
          : device.registered && device.permission === "granted"
            ? "On. You'll get notifications here even when the app is closed."
            : "Off on this device.";

    return (
      <Card>
        <CardHeader>
          <div>
            <CardTitle as="h2">Notifications on your devices</CardTitle>
            <CardDescription>{config.data?.enabled === false ? "Push notifications are switched off for this workspace." : status}</CardDescription>
          </div>
          {config.data?.enabled ? (
            <div className="flex shrink-0 gap-2">
              {device.registered && device.permission === "granted" ? (
                <>
                  {allowTest ? (
                    <Button variant="secondary" size="sm" loading={test.isPending} onClick={() => test.mutate()}>
                      Send a test
                    </Button>
                  ) : null}
                  <Button variant="ghost" size="sm" loading={disable.isPending} onClick={() => disable.mutate(config.data)}>
                    Turn off here
                  </Button>
                </>
              ) : device.permission === "default" || device.permission === "granted" ? (
                <Button size="sm" loading={enable.isPending} onClick={() => config.data && enable.mutate(config.data)}>
                  Turn on here
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardHeader>
        {devices.isPending ? (
          <Skeleton className="h-10" />
        ) : devices.isError ? (
          <p role="alert" className="text-sm text-danger">
            Your devices could not be loaded.
          </p>
        ) : devices.data.length === 0 ? (
          <p className="text-sm text-subtle">No device gets your notifications yet.</p>
        ) : (
          <ul aria-label="Your devices" className="divide-y divide-edge-subtle">
            {devices.data.map((d) => (
              <li key={d.id} className="flex items-center gap-3 py-2">
                <Monitor className="size-4 shrink-0 text-subtle" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-heading">
                    {d.deviceLabel ?? "A browser"} <Badge tone="neutral">{d.app}</Badge>
                  </p>
                  <p className="text-xs text-muted">
                    Last used <time dateTime={d.lastSeenAt}>{new Date(d.lastSeenAt).toLocaleDateString()}</time>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${d.deviceLabel ?? "this device"}`}
                  loading={remove.isPending && remove.variables === d.id}
                  onClick={() => remove.mutate(d.id)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  }

  return {
    pushKeys,
    usePushConfig,
    usePushDevices,
    useEnablePush,
    useDisablePush,
    useSendTestPush,
    useRemoveDevice,
    removeThisDevice,
    PushBootstrap,
    PushSoftAsk,
    PushDevicesCard,
  };
}

export type Push = ReturnType<typeof createPush>;
